import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, Save } from "lucide-react";
import { toast } from "sonner";
import { localDate } from "@/lib/localDate";

const OPTS = [["present", "P"], ["absent", "A"], ["leave", "L"]];
const ON = { present: "bg-emerald-600 text-white border-emerald-600", absent: "bg-rose-600 text-white border-rose-600", leave: "bg-amber-500 text-white border-amber-500" };
const OFF = { present: "bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50", absent: "bg-white text-rose-600 border-rose-200 hover:bg-rose-50", leave: "bg-white text-amber-600 border-amber-200 hover:bg-amber-50" };
const today = () => localDate();

export function AttendanceTab({ staff }) {
  const { user } = useAuth(); const qc = useQueryClient();
  const isSuper = user?.role === "super_admin";
  const [date, setDate] = useState(today());
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: saved = {} } = useQuery({ queryKey: ["att-day", date], queryFn: async () => (await api.get("/staff/attendance/day", { params: { date } })).data });
  useEffect(() => { setDraft({}); }, [date]);
  const changes = Object.entries(draft).filter(([sid, st]) => saved[sid]?.status !== st);
  async function save() {
    if (!changes.length) { toast.info("Nothing to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/staff/attendance/bulk", { date, entries: changes.map(([staff_id, status]) => ({ staff_id, status })) });
      data.errors.length ? toast.error(data.errors[0].error) : toast.success(`Attendance saved for ${data.saved} staff`);
      setDraft({}); qc.invalidateQueries({ queryKey: ["att-day", date] }); qc.invalidateQueries({ queryKey: ["payroll"] });
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  }
  return (
    <Card className="p-4 border-slate-200" data-testid="attendance-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div><label className="text-sm font-medium text-slate-600">Attendance Date</label>
          <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="mt-1 w-44" data-testid="att-date" /></div>
        <p className="text-xs text-slate-500 flex items-center gap-1"><Lock className="h-3.5 w-3.5" />One entry per staff per day. Saved attendance is locked{isSuper ? " — you can correct it as Super Admin." : "; only Super Admin can correct it."}</p>
        <Button className="bg-[#1A2A5E]" onClick={save} disabled={saving} data-testid="att-save"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving..." : `Save Attendance${changes.length ? ` (${changes.length})` : ""}`}</Button>
      </div>
      <div className="divide-y">
        {(staff || []).map((s) => {
          const cur = draft[s.id] || saved[s.id]?.status; const locked = !!saved[s.id] && !isSuper;
          return (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`att-row-${s.id}`}>
              <div><div className="font-semibold text-sm">{s.name} <span className="text-xs text-slate-400">{s.staff_id && `(${s.staff_id})`}</span></div>
                <div className="text-xs text-slate-400">{[s.designation, saved[s.id] && `saved by ${saved[s.id].marked_by || "—"}`].filter(Boolean).join(" · ")}</div></div>
              <div className="flex items-center gap-1.5">
                {OPTS.map(([v, l]) => (
                  <button key={v} disabled={locked} onClick={() => setDraft({ ...draft, [s.id]: v })} data-testid={`att-${v}-${s.id}`}
                    className={`h-9 w-11 rounded-lg border text-sm font-bold transition-colors disabled:cursor-not-allowed ${cur === v ? ON[v] : OFF[v]} ${locked && cur !== v ? "opacity-40" : ""}`}>{l}</button>
                ))}
                <span className="w-5 ml-1 flex justify-center">{locked && <Lock className="h-4 w-4 text-slate-400" data-testid={`att-locked-${s.id}`} />}</span>
              </div>
            </div>
          );
        })}
        {!(staff || []).length && <div className="text-center text-slate-400 py-8 text-sm">No staff yet</div>}
      </div>
    </Card>
  );
}
